import { ABBREVIATION, PRIORITY, PROGRAM, PROGRAM_DEPARTMENT, PROGRAM_DIVISION, REQUEST } from '../../../static/keyWords.js';
import { NAME_CONVENTIONS } from '../../NameConvention/nameConventionHelpers.js';

const FILTER_TYPES = {
  FUND: 'FUND',
  DEPARTMENT: 'DEPARTMENT',
  BPA: 'BPA',
  DIVISION: 'DIVISION',
  QUARTILE: 'QUARTILE',
  COST_TYPE: 'COST_TYPE',
  RESULT: 'RESULT',
  SERVICE_TYPE: 'SERVICE_TYPE',
  COMPLEXITY: 'COMPLEXITY',
  TIMELINE: 'TIMELINE',
  PROGRAM_CHANGE: 'PROGRAM_CHANGE',
  ACCOUNT_TYPE: 'ACCOUNT_TYPE',
  ACCOUNT_CODE: 'ACCOUNT_CODE',
  QUESTIONS: 'QUESTIONS',
  DURATION_TYPE: 'DURATION_TYPE',
  REVENUE: 'REVENUE',
  QUARTILE_DIFF: 'QUARTILE_DIFF',
  COST_ALLOCATION_PERSONNEL: 'COST_ALLOCATION_PERSONNEL',
  COST_ALLOCATION_NON_PERSONNEL: 'COST_ALLOCATION_NON_PERSONNEL',
  COST_ALLOCATION_REVENUE: 'COST_ALLOCATION_REVENUE',
  STATUS: 'STATUS',
  // Data Visualization
  DV_DIVISION: 'DV_DIVISION',
  DV_SECTION: 'DV_SECTION',
  DV_QUARTILE: 'DV_QUARTILE',
  DV_PERSONNEL: 'DV_PERSONNEL',
  DV_NONPERSONNEL: 'DV_NONPERSONNEL',
  DV_REVENUE: 'DV_REVENUE',
  DV_BUDGET: 'DV_BUDGET',
  DV_RESULT: 'DV_RESULT',
  DV_FUND: 'DV_FUND',
  //budgeter
  REQUEST_STATUS: 'REQUEST_STATUS',
  BUDGETER_PERSONNEL: 'BUDGETER_PERSONNEL',
  BUDGETER_NON_PERSONNEL: 'BUDGETER_NON_PERSONNEL',
  BUDGETER_ORGANIZATION_LEVELS: 'BUDGETER_ORGANIZATION_LEVELS',
  BUDGETER_GROUPS_FILTER: 'BUDGETER_GROUPS_FILTER',
  REQUEST_TYPE: 'REQUEST_TYPE',
  OFFSET: 'OFFSET',
  COST_BENCHMARK_COST: 'COST_BENCHMARK_COST'
};

const adaptFieldName = (type) => {
  switch (type) {
    case FILTER_TYPES.DV_DIVISION:
      return 'Department';
    case FILTER_TYPES.DV_SECTION:
      return 'Division';
    case FILTER_TYPES.DV_QUARTILE:
      return 'Quartile';
    case FILTER_TYPES.DV_PERSONNEL:
      return 'Personnel';
    case FILTER_TYPES.DV_NONPERSONNEL:
      return 'NonPersonnel';
    case FILTER_TYPES.DV_REVENUE:
      return 'Revenue';
    case FILTER_TYPES.DV_BUDGET:
      return 'Budget';
    case FILTER_TYPES.DV_RESULT:
      return 'Result';
    case FILTER_TYPES.DV_FUND:
      return 'Fund';
    case FILTER_TYPES.SERVICE_TYPE:
      return 'Service Type';
    case FILTER_TYPES.BPA:
      return 'BPA';
    default:
      return 'Unknown';
  }
};

const adaptFilterData = (filter, fieldName) => {
  if (filter.value && Array.isArray(filter.value)) {
    return {
      [fieldName]: filter.value.map((element) => element.name),
    };
  }

  if (filter.options && Array.isArray(filter.options)) {
    let complexFilter = {};
    for (const subFilter of filter.options) {
      complexFilter = {
        ...complexFilter,
        [subFilter.name]: subFilter.value,
      };
    }

    return complexFilter;
  }

  return [];
};

const adaptNewFiltersStructure = (appliedFilters) => {
  let _appliedFilters = {};
  for (const filter of appliedFilters) {
    const fieldName = adaptFieldName(filter.type);
    _appliedFilters = {
      ..._appliedFilters,
      [fieldName]: adaptFilterData(filter, fieldName),
    };
  }

  return _appliedFilters;
};

const removeSimpleFilterOption = (setAppliedFilters, appliedFilters, index) => {
  setAppliedFilters([
    ...appliedFilters.slice(0, index),
    ...appliedFilters.slice(index + 1),
  ]);
};

const selectSimpleFilter = (
  setAppliedFilters,
  appliedFilters,
  index,
  type,
  selectedFilters
) => {
  setAppliedFilters([
    ...appliedFilters.slice(0, index),
    { type: type, isCompound: false, value: selectedFilters },
    ...appliedFilters.slice(index + 1),
  ]);
};

const addSimpleFilterOptionAndSelectFilter = (
  setAppliedFilters,
  appliedFilters,
  type,
  selectedFilters
) => {
  setAppliedFilters([
    ...appliedFilters,
    {
      type: type,
      isCompound: false,
      value: selectedFilters
    },
  ]);
};

const getNumbersOfAppliedFilters = (innerAppliedFilters) => {
  let _numOfAppliedFilters = 0;
  for (const appliedFilterObject of innerAppliedFilters) {
    if (
      appliedFilterObject &&
      appliedFilterObject.value &&
      appliedFilterObject.value.length
    ) {
      _numOfAppliedFilters += appliedFilterObject.value.length;
    } else if (
      appliedFilterObject &&
      appliedFilterObject.options &&
      appliedFilterObject.options.length
    ) {
      for (const appliedFilterObjectOption of appliedFilterObject.options) {
        _numOfAppliedFilters += appliedFilterObjectOption.value.length;
      }
    }
  }
  return _numOfAppliedFilters;
};

const getFilterNumberMessage = (numOfAppliedFilters) => {
  return `${numOfAppliedFilters} ${numOfAppliedFilters === 1 ? 'filter' : 'filters'
  } applied`;
};

const removeCompoundFilters = (
  setAppliedFilters,
  appliedFilters,
  index,
  name
) => {
  let tempAppliedFilters = structuredClone(appliedFilters);
  const appliedFilter = tempAppliedFilters[index];
  const foundOptionIndex = appliedFilter.options.findIndex(
    (x) => x.name === name
  );
  if (foundOptionIndex > -1) {
    appliedFilter.options.splice(foundOptionIndex, 1);
    if (!appliedFilter.options.length) {
      removeCompoundFilterOption(tempAppliedFilters, index);
    }
  }
  setAppliedFilters(tempAppliedFilters);
};

const removeCompoundFilterOption = (appliedFilters, index) => {
  appliedFilters.splice(index, 1);
};

const selectCompoundFilter = (
  setAppliedFilters,
  appliedFilters,
  index,
  selectedFilters,
  name
) => {
  const appliedFilter = structuredClone(appliedFilters[index]);
  const foundOption = appliedFilter.options.find((x) => x.name === name);
  if (foundOption) {
    foundOption.value = selectedFilters;
  } else {
    appliedFilter.options.push({ name: name, value: selectedFilters });
  }
  setAppliedFilters([
    ...appliedFilters.slice(0, index),
    appliedFilter,
    ...appliedFilters.slice(index + 1),
  ]);
};

const addCompoundFilterOptionAndSelectFilter = (
  setAppliedFilters,
  appliedFilters,
  type,
  name,
  selectedFilters
) => {
  setAppliedFilters([
    ...appliedFilters,
    {
      type: type,
      isCompound: true,
      options: [{ name: name, value: selectedFilters }],
    },
  ]);
};

const getFilterLabels = (filterType, params, nameConventions = NAME_CONVENTIONS, costModel) => {
  const orgLevel1 = (costModel.organizationLevelTypes || []).find(level => level.levelNumber === 1);
  const orgLevel2 = (costModel.organizationLevelTypes || []).find(level => level.levelNumber === 2);

  const boxChoiceTypes = {};
  if (params?.insightBoxChoiceTypes) {
    boxChoiceTypes.complexity = params.insightBoxChoiceTypes[0].name;
    boxChoiceTypes.timeline = params.insightBoxChoiceTypes[1].name;
  }

  switch (filterType) {
    case FILTER_TYPES.FUND:
      return 'Fund';
    case FILTER_TYPES.DEPARTMENT:
      return nameConventions[PROGRAM_DEPARTMENT].alias;
    case FILTER_TYPES.BPA:
      return 'BPA';
    case FILTER_TYPES.DIVISION:
      return nameConventions[PROGRAM_DIVISION].alias;
    case FILTER_TYPES.QUARTILE:
      return 'Quartile';
    case FILTER_TYPES.COST_TYPE:
      return 'Cost type';
    case FILTER_TYPES.RESULT:
      return nameConventions[PRIORITY].alias;
    case FILTER_TYPES.SERVICE_TYPE:
      return 'Service type';
    case FILTER_TYPES.COMPLEXITY:
      return boxChoiceTypes.complexity || 'Complexity';
    case FILTER_TYPES.TIMELINE:
      return boxChoiceTypes.timeline || 'Timeline';
    case FILTER_TYPES.PROGRAM_CHANGE:
      return nameConventions[PROGRAM].alias + ' Change';
    case FILTER_TYPES.ACCOUNT_TYPE:
      return 'Account type';
    case FILTER_TYPES.ACCOUNT_CODE:
      return 'Account code';
    case FILTER_TYPES.QUESTIONS:
      return 'Questions';
    case FILTER_TYPES.DURATION_TYPE:
      return 'Duration type';
    // case FILTER_TYPES.PERSONNEL:
    //   return 'Personnel';
    // case FILTER_TYPES.NON_PERSONNEL:
    //   return 'NonPersonnel';
    // case FILTER_TYPES.REVENUE:
    //   return 'Revenue';
    case FILTER_TYPES.QUARTILE_DIFF:
      return 'QuartileDiffs';
    case FILTER_TYPES.COST_ALLOCATION_PERSONNEL:
      return 'Personnel';
    case FILTER_TYPES.COST_ALLOCATION_NON_PERSONNEL:
      return 'NonPersonnel';
    case FILTER_TYPES.COST_ALLOCATION_REVENUE:
      return 'Revenue';
    // Data Visualization
    case FILTER_TYPES.DV_DIVISION:
      return orgLevel1?.name || 'Division';
    case FILTER_TYPES.DV_SECTION:
      return orgLevel2?.name || 'Section';
    case FILTER_TYPES.DV_QUARTILE:
      return 'Quartile';
    case FILTER_TYPES.DV_PERSONNEL:
      return 'Personnel';
    case FILTER_TYPES.DV_NONPERSONNEL:
      return 'NonPersonnel';
    case FILTER_TYPES.DV_REVENUE:
      return 'Revenue';
    case FILTER_TYPES.DV_BUDGET:
      return 'Budget';
    case FILTER_TYPES.DV_RESULT:
      return nameConventions[PRIORITY].alias;
    case FILTER_TYPES.DV_FUND:
      return 'Fund';
    // budgeter
    case FILTER_TYPES.OFFSET:
      return 'Offset';
    case FILTER_TYPES.REQUEST_STATUS:
    case FILTER_TYPES.STATUS:
      return 'Status';
    case FILTER_TYPES.BUDGETER_PERSONNEL:
      return 'Personnel category';
    case FILTER_TYPES.BUDGETER_NON_PERSONNEL:
      return 'NonPersonnel category';
    case FILTER_TYPES.BUDGETER_ORGANIZATION_LEVELS:
      return 'Organizational levels';
    case FILTER_TYPES.BUDGETER_GROUPS_FILTER:
      return 'Groups';
    case FILTER_TYPES.REQUEST_TYPE:
      return nameConventions[REQUEST].alias + ' type';
    case FILTER_TYPES.COST_BENCHMARK_COST:
      return 'Cost';
    default:
      return 'N/A';
  }
};

const getChipCount = (filters, appliedFilters, appliedType, filterType) => {
  return filters.reduce((count, filter) => {
    const isApplied = appliedFilters.find((applied) => applied[appliedType] === filter[filterType]);
    return isApplied ? count + isApplied.value.length : count;
  }, 0);
};

const compoundAutocompleteValue = (innerAppliedFilters, setting, option) => {
  const appliedFilter = innerAppliedFilters.find(inner => inner.type === setting.type);
  if (!appliedFilter) return '';

  const optionFilter = appliedFilter.options.find(opt => opt.name === option[ABBREVIATION]);
  if (optionFilter) {
    if (optionFilter.value.length > 1) {
      const result = [];
      optionFilter.value.forEach(val => {
        result.push({ label: val.name, value: { name: val.name, id: val.id } });
      });
      return result;
    } else {
      return { label: optionFilter.value[0].name, value: { name: optionFilter.value[0].name, id: optionFilter.value[0].id } };
    }
  }
};

const simpleAutocompleteValue = (innerAppliedFilters, setting) => {
  const appliedFilter = innerAppliedFilters.find(inner => inner.type === setting.type);
  if (!appliedFilter) return '';

  if (appliedFilter.value.length > 1) {
    const result = [];
    appliedFilter.value.forEach(val => {
      result.push({ label: val.name, value: val });
    });
    return result;
  } else {
    return { label: appliedFilter.value[0].name, value: appliedFilter.value[0] };
  }
};

const getAllFilterValuesWithPaging = (allFilters, setting) => {
  const appliedFilter = allFilters.find(inner => inner.type === setting.type);

  if (!appliedFilter) return setting.options;

  const selectedIds = (appliedFilter.value || []).map(filter => filter.id);
  const prioritySet = new Set(selectedIds);

  const result = setting.options;

  result.sort((a, b) => {
    const aIsPriority = prioritySet.has(a.id);
    const bIsPriority = prioritySet.has(b.id);

    if (aIsPriority && !bIsPriority) return -1;
    if (!aIsPriority && bIsPriority) return 1;

    return 0;
  });

  return result;
};


const getAllCompoundFilterValuesWithPaging = (allFilters, setting, option) => {
  const appliedFilter = allFilters.find(inner => inner.type === setting.type);
  if (!appliedFilter) { return option.value; }

  const appliedFilterOption = (appliedFilter.options || []).find(appliedOption => appliedOption.name === option.abbreviation);
  if (!appliedFilterOption) { return option.value; }

  const prioritySet = new Set(appliedFilterOption.value.map(value => value.id));

  const result = option.value;

  result.sort((a, b) => {
    const aIsPriority = prioritySet.has(a.value.id);
    const bIsPriority = prioritySet.has(b.value.id);

    if (aIsPriority && !bIsPriority) return -1;
    if (!aIsPriority && bIsPriority) return 1;

    return 0;
  });

  return result;
};

export {
  FILTER_TYPES,
  removeSimpleFilterOption,
  selectSimpleFilter,
  addSimpleFilterOptionAndSelectFilter,
  adaptNewFiltersStructure,
  getNumbersOfAppliedFilters,
  getFilterNumberMessage,
  removeCompoundFilters,
  removeCompoundFilterOption,
  selectCompoundFilter,
  addCompoundFilterOptionAndSelectFilter,
  getFilterLabels,
  getChipCount,
  compoundAutocompleteValue,
  simpleAutocompleteValue,
  getAllFilterValuesWithPaging,
  getAllCompoundFilterValuesWithPaging
};
